import {inject, Injectable} from "@angular/core";
import {environment} from "../../../environments/environment.dev";
import {clamp} from "../../shared/util/clamp";
import {AssemblyFrameInput} from "../models/assembly-frame-input";
import {AssemblySpacingInput} from "../models/spacing-input";
import {AssemblySpacingComponentService} from "./assembly-spacing-component.service";


@Injectable({providedIn: 'root'})
export class FrameAssemblyService {
    private assemblySpacingComponentService = inject(AssemblySpacingComponentService);

    public provideFramePositionsLamellaArea(assemblyArray:any): Array<{position: number, reinforced: boolean}> {
        return assemblyArray
            .filter(this.isFrameSpanInput)
            .map((item: any) => ({position: item.spanValue, reinforced: item.hasReinforcement}));
    }

    public provideAssemblyArrayLamellaArea( id: string, span: number, autoFramesConfig: any ): Array<AssemblyFrameInput|AssemblySpacingInput>{

        const assemblyArray: any[] = [];

        while (assemblyArray.length < autoFramesConfig.count * 2 + 1){

            if ( assemblyArray.length % 2 !== 0) {
                assemblyArray.push(this.createFrameSpanInputsLamellaArea(id, assemblyArray.length, assemblyArray, span));

            } else {
                assemblyArray.push(assemblyArray.length === 0 || assemblyArray.length === autoFramesConfig.count * 2
                    ? this.assemblySpacingComponentService.createSpacingComponentInputLamellaArea(id, assemblyArray.length, span, autoFramesConfig,true)
                    : this.assemblySpacingComponentService.createSpacingComponentInputLamellaArea(id, assemblyArray.length, span, autoFramesConfig,false));

            }
        }
        const arraySpan = assemblyArray.reduce((acc, curr: any) => this.isSpacingInput(curr) ? acc + curr.span : acc, 0);
        if(span !== arraySpan){ assemblyArray[2].span += span - arraySpan; }
        this.updateFramePositionsLamellaArea(assemblyArray);

        return assemblyArray;

    }

    public provideAssemblyArrayLamellaAreaFromFramePositions( id: string, span: number, framePositions: Array<{position: number, reinforced: boolean}>, autoFramesConfig: any ): Array<any> {
        const assemblyArray: any[] = [];

        while (assemblyArray.length < framePositions.length * 2 + 1){

            if ( assemblyArray.length % 2 !== 0) {
                assemblyArray.push(this.createFrameSpanInputsFromFrameSpansLamellaArea(id, assemblyArray.length, span, framePositions));

            } else {
                assemblyArray.push(assemblyArray.length === 0 || assemblyArray.length === autoFramesConfig.count * 2
                    ? this.assemblySpacingComponentService.createSpacingComponentInputFromFramePositionsLamellaArea(id, assemblyArray.length, span, framePositions, autoFramesConfig, true)
                    : this.assemblySpacingComponentService.createSpacingComponentInputFromFramePositionsLamellaArea(id, assemblyArray.length, span, framePositions, autoFramesConfig, false));

            }
        }

        const arraySpan = assemblyArray.reduce((acc, curr: any) => this.isSpacingInput(curr) ? acc + curr.span : acc, 0);
        if(span !== arraySpan){ assemblyArray[2].span += span - arraySpan; }
        this.updateFramePositionsLamellaArea(assemblyArray);

        return assemblyArray;
    }

    /**
     * Calculate the needed frames for the given span. span can be on y or x -axis.
     * @param span - lamella area width or height depending on installation direction
     * @param maxFrameSpan
     * @param maxOverflowSpan
     */
    watchNeededFramesLamellaArea(span: number, maxFrameSpan : number = environment.FRAME_MAX_SPAN, maxOverflowSpan: number = environment.FRAME_OVERFLOW_SPACE) {



            const remainder = span % maxFrameSpan;
            const overFlowable  = remainder < maxOverflowSpan * 2;

            const count = overFlowable
                ? Math.floor(span / maxFrameSpan) + 1
                : Math.floor(span / maxFrameSpan) + 2 ;

            const centerSpanWidth=  Math.round( ( span - ( overFlowable ? remainder : 0) ) / (count - 1) ) ;

            const endSpanWidth = overFlowable ? remainder/2 : 0;

            return {
                count,
                centerSpanWidth,
                endSpanWidth
            };



    }

    public createFrameSpanInputsLamellaArea(id: string, index: number, currentArray: any[], lamellaAreaSpan: number, previousValue?: any) {

        const hasReinforcement = previousValue?.hasReinforcement
            ?? false ; //[boolean]

        const spanValue = previousValue?.spanValue
            ?? currentArray.reduce((acc, curr: any) => this.isSpacingInput(curr) && currentArray.indexOf(curr) < index ? acc + curr.span : acc, 0); //[mm]

        const spanPosition = previousValue?.spanPosition
            ?? currentArray.reduce((acc, curr: any) => this.isSpacingInput(curr) && currentArray.indexOf(curr) < index && curr.relativeWidth ? acc + curr.relativeWidth : acc, 0) //[px]

        const parentElementWidth = previousValue?.parentElementWidth

        return {
            id, //[string]
            index, //[int]
            hasReinforcement, //[boolean]
            spanValue, //[mm]
            spanPosition, //[px]
            lamellaAreaSpan, //[mm]
            parentElementWidth
        };

    }

    public createFrameSpanInputsFromFrameSpansLamellaArea(id: string, index: number, lamellaAreaSpan: number, framePositions: any[]) {
        const framePositionsWithIndexes = framePositions.map((f: any, idx: number) => ({...f, index: (1 + ( idx * 2 ))}))

        const hasReinforcement = framePositionsWithIndexes.find((item: any) => item.index === index)?.reinforced ?? false;
        const spanValue: number = framePositionsWithIndexes.find((item: any) => item.index === index)?.position ?? 0;

        const spanPosition = 0; //TODO: remove
        const parentElementWidth = 0; //TODO: remove

        return {
            id, //[string]
            index, //[int]
            hasReinforcement, //[boolean]
            spanValue, //[mm]
            spanPosition, //[px] //TODO: remove
            lamellaAreaSpan, //[mm]
            parentElementWidth //TODO: remove
        };

    }

    public isSpacingInput(object: any): object is AssemblySpacingInput  { return object.span !== undefined }

    public isFrameSpanInput(object: any): object is AssemblyFrameInput { return object.hasReinforcement !== undefined }

    public calculateFrameSpanValuesOnInputLamellaArea(index: number, targetSpan: number, assemblyArrayRef: any){

        let assemblyArray = [ ...(assemblyArrayRef.map((i:any) => ({ ...i }) )) ];
        let totalSpan = assemblyArray.filter(this.isSpacingInput).reduce((acc: number, curr: any) => acc + curr.span, 0);
        const current = assemblyArray.find((item: any) => item.index === index);

        const spacingComponents = assemblyArray
            .filter(this.isSpacingInput)
            .filter((item: any) => item.index > index && !item.reinforced);

        //value that can be deducted from after the current span
        const maxLoanableSpan = this.maxLoanableSpanLamellaArea(index, assemblyArray);

        //value that can be added to after the current span
        const maxGiveableSpan = this.maxGivableSpanLamellaArea(index, assemblyArray);

        //value that can be deducted from the current span
        const maxDeductibleSpan = Math.min(current.span - current.minSpan, maxGiveableSpan);

        let neededSpan = targetSpan >= current.span
            ? Math.min(targetSpan - current.span, maxLoanableSpan)
            : Math.min(targetSpan - current.span , maxDeductibleSpan);

        if( neededSpan === 0 || (maxLoanableSpan === 0  && neededSpan > 0 )  || (maxGiveableSpan === 0 && neededSpan < 0) ){
            assemblyArray[index].span = targetSpan;
            return assemblyArray;
        }

        for (let spacingComponent of spacingComponents) {

            const growing = neededSpan > 0;

            if( neededSpan === 0 ) { break }

            const spanComponent = assemblyArray[spacingComponent.index];
            const spanAtIndex = spanComponent.span;
            const spanComponentAtModifyingIndex = assemblyArray[index]; //original modifying target
            const spanAtModifyingIndex = spanComponentAtModifyingIndex.span;

            const spanValueChange = clamp(
                growing ? this.spanDifferenceLamellaArea(spanComponent) : Math.min(Math.abs(neededSpan), maxDeductibleSpan),
                growing ? 0 : maxDeductibleSpan,
                growing ? maxLoanableSpan : maxGiveableSpan
            );

            const spanAdjustment = growing ? Math.min(spanValueChange, neededSpan) : Math.abs(neededSpan);

            spanComponent.span = spanAtIndex + (growing ? -spanAdjustment : spanAdjustment);
            spanComponentAtModifyingIndex.span = spanAtModifyingIndex + (growing ? spanAdjustment : -spanAdjustment);
            totalSpan += growing ? spanAdjustment : -spanAdjustment;
            neededSpan -= growing ? spanAdjustment : -spanAdjustment;
        }

        this.updateFramePositionsLamellaArea(assemblyArray);

        return assemblyArray;

    }

    public maxLoanableSpanLamellaArea(index: number, assemblyArray: any ): number {
        return assemblyArray
            .filter(this.isSpacingInput)
            .filter((item: any) => item.index > index && !item.overflowSpace)
            .reduce((acc: number, item: any) => acc + this.spanDifferenceLamellaArea(item), 0);
    }


    public maxGivableSpanLamellaArea(index: number, assemblyArray: Array<any>): number {

        const nextSpan: any = assemblyArray
            .filter(this.isSpacingInput)
            .find( (item: any) => item.index === index+2 && !item.overflowSpace )

        if(nextSpan === undefined){ return 0; }

        return nextSpan.overflowSpace === true
            ? nextSpan.maxSpan - nextSpan.span
            : Number.MAX_SAFE_INTEGER;
    }

    public calculateFrameSpanValuesOnDrag(
        index: number,
        cursorX: number,
        element: HTMLElement,
        assemblyArray: Array<AssemblyFrameInput|AssemblySpacingInput>,

        ): Array<AssemblyFrameInput|AssemblySpacingInput> {

        const prev = assemblyArray.find((item: any) => item.index === index - 1)! as AssemblySpacingInput;
        const next = assemblyArray.find((item: any) => item.index === index + 1)! as AssemblySpacingInput;

        //if both spans are at their minimum, do nothing
        if(prev.span() === prev.minSpan() && next.span() === next.minSpan()){ return assemblyArray; }

        const span = prev.span() + next.span();

        const {left, right} = this.calculateCursorConstraints(element, prev, next);

        const cursorValue = clamp(cursorX, left, right);

        const { leftRelativeWidth, rightRelativeWidth, sharedRelativeWidth}
            = this.calculateRelativeWidths(cursorValue, left, right, prev, next, element);

        const { newLeftSpan, newRightSpan}
            = this.calculateAdjancedSpanValues(leftRelativeWidth, rightRelativeWidth, sharedRelativeWidth, prev, next, cursorX, element, span);

        (assemblyArray[index-1] as AssemblySpacingInput).span.set(newLeftSpan);
        (assemblyArray[index+1] as AssemblySpacingInput).span.set(newRightSpan);

        assemblyArray.forEach( ( item: any, idx: number, arr: any ) => {
            if(this.isFrameSpanInput(item)){
                const val: number = element.parentElement.children[idx-1].getBoundingClientRect().right;
                (assemblyArray[idx] as AssemblyFrameInput).spanPosition.set(val);
            }
        });

        return assemblyArray;
    }

    public splitSpanLamellaArea(index: number, assemblyArrayRef: any, span: number) {

        let assemblyArray = [ ...(assemblyArrayRef.map((i:any) => ({ ...i }) )) ];

        const rep = assemblyArray[index];

        const replacement = [
            this.assemblySpacingComponentService.createSpacingComponentInputLamellaArea(rep.id, index, rep.lamellaAreaSpan, rep.frameSpacingConfig),
            this.assemblySpacingComponentService.createSpacingComponentInputLamellaArea(rep.id, index + 2, rep.lamellaAreaSpan, rep.frameSpacingConfig),
        ];

        replacement.forEach((item, i) => {item.span = Math.round(rep.span / 2)});

        //if span is odd, add one to the first replacement
        if(rep.span % 2 !== 0){ replacement[0].span += 1; }

        //replace spacings
        assemblyArray.splice(index, 1, ...replacement);

        //reorder indexes
        assemblyArray.forEach( ( item: any, i: number ) => item.index = i );

        //hox this method need assemblyArray with organized indexes
        const newFrame = this.createFrameSpanInputsLamellaArea(rep.id, index + 1, assemblyArray, rep.lamellaAreaSpan);

        //inject new frame
        assemblyArray.splice(index+1, 0, newFrame);

        //reorder indexes
        assemblyArray.forEach( ( item: any, i: number ) => item.index = i );

        this.updateFramePositionsLamellaArea(assemblyArray);

        return assemblyArray;
    }

    public conjoinSpansLamellaArea(index: number,  assemblyArrayRef: any) {

        let assemblyArray = [ ...(assemblyArrayRef.map((i:any) => ({ ...i }) )) ];

        const lastSpanModifiableSpan = index>=assemblyArray.length-3;

        const curr = assemblyArray[index];

        const prev = assemblyArray[index - 2];

        const next = assemblyArray[index + 2];

        const span = lastSpanModifiableSpan
            ? curr.span + prev.span
            : curr.span + next.span;

        assemblyArray.splice( lastSpanModifiableSpan ? index : index-1 , 2);

        assemblyArray.forEach((item: any, i: number) => item.index = i);

        lastSpanModifiableSpan
            ? assemblyArray[index-2].span = span
            : assemblyArray[index].span = span;

        this.updateFramePositionsLamellaArea(assemblyArray);

        return assemblyArray;
    }

    public reinforceFrames(spacingIndex: number, assemblyArray: Array<AssemblyFrameInput|AssemblySpacingInput>): Array<AssemblyFrameInput|AssemblySpacingInput> {

        [spacingIndex-1, spacingIndex+1].forEach( (idx) => {
            const leftSpacing = assemblyArray.find((item: any) => item.index === idx-1)! as AssemblySpacingInput;
            const rightSpacing = assemblyArray.find((item: any) => item.index === idx+1)! as AssemblySpacingInput;
            const isReinforcementActive = leftSpacing.reinforced() || rightSpacing.reinforced();
            (assemblyArray[idx] as AssemblyFrameInput).hasReinforcement.set(isReinforcementActive);
        })

        return assemblyArray;
    }

    public reinforceFrame(index: number, assemblyArray: Array<AssemblyFrameInput|AssemblySpacingInput>): Array<AssemblyFrameInput|AssemblySpacingInput> {
        (assemblyArray[index] as AssemblyFrameInput).hasReinforcement.set(!(assemblyArray[index] as AssemblyFrameInput).hasReinforcement());
        return assemblyArray;
    }

    public overflowSpanChangeLamellaArea(index: number, targetSpan: number, lamellaSpan: number,  assemblyArray: any){
        return index === 0
            ? this.calculateFrameSpanValuesOnInputLamellaArea(index, targetSpan, assemblyArray)
            : this.lastIndexSpacingChangeLamellaArea(index, targetSpan, lamellaSpan, assemblyArray);
    }

    private lastIndexSpacingChangeLamellaArea(index: number, targetSpan: number, lamellaSpan: number,  assemblyArrayRef: any){
        let assemblyArray = [ ...(assemblyArrayRef.map((i:any) => ({ ...i }) )) ];
        const current = assemblyArray.find((item: any) => item.index === index);
        let currentSpan = current.span;

        const spacingComponents: any[] = assemblyArray
            .filter(this.isSpacingInput)
            .filter((item: any) => item.index < index && !item.reinforced)
            .sort((a:any,b:any) => b.index - a.index);

        let neededSpan = targetSpan >= currentSpan
            ? Math.min(targetSpan - currentSpan, current.maxSpan - currentSpan)
            : Math.min(targetSpan - currentSpan, currentSpan - current.minSpan);

        if( neededSpan === 0 || (current.maxSpan === current.minSpan && neededSpan > 0 )  || (current.maxSpan === current.minSpan && neededSpan < 0) ){
            assemblyArray[index].span = currentSpan;
            return assemblyArray;
        }

        const growing = neededSpan > 0;

        for (let spacingComponent of spacingComponents) {
            const spanAtIndex = spacingComponent.span;

            const spanComponentAtModifyingIndex = assemblyArray[index] ; //original modifying target
            const spanAtModifyingIndex = spanComponentAtModifyingIndex.span;
            const spanValueChangeRoof = current.maxSpan - currentSpan;
            const spanValueChangeFloor = currentSpan - current.minSpan;

            const spanValueChange = clamp(
                growing ? this.spanDifferenceLamellaArea( spacingComponent ) : Math.min( Math.abs(neededSpan), spanValueChangeRoof ),
                spanValueChangeRoof,
                growing ? spanValueChangeRoof : spanValueChangeFloor
            );

            const spanAdjustment = growing ? Math.min(spanValueChange, neededSpan) : Math.abs(neededSpan);
            spacingComponent.span =  spanAtIndex + (growing ? -spanAdjustment : spanAdjustment);
            spanComponentAtModifyingIndex.span = spanAtModifyingIndex + (growing ? spanAdjustment : -spanAdjustment);
            currentSpan += growing ? spanAdjustment : -spanAdjustment;
            neededSpan -= growing ? spanAdjustment : -spanAdjustment;
        }

        this.updateFramePositionsLamellaArea(assemblyArray);

        return assemblyArray
    }

    public updateFramePositionsLamellaArea(assemblyArray: any){
        assemblyArray.forEach((item: any, i: number) => {
            if(this.isFrameSpanInput(item)){
                item.spanValue = assemblyArray.reduce((acc: number, curr: any) => this.isSpacingInput(curr) && curr.index < i ? acc + (curr as any).span : acc, 0);
            }
        });
    }

    private spanDifferenceLamellaArea(item: any) {
        return item.span - item.minSpan;
    }

    private calculateRelativeWidths(cursorValue: number, left: number, right: number, prev: AssemblySpacingInput, next: AssemblySpacingInput, element: HTMLElement) {
        const leftRelativeWidth = prev ? Math.max( (prev.overflowSpace() ? 0 : element.clientWidth * 2) , cursorValue - left) : 0;
        const rightRelativeWidth = next ? Math.max( (next.overflowSpace() ? 0 : element.clientWidth * 2) , right - cursorValue) : 0;
        const sharedRelativeWidth = leftRelativeWidth + rightRelativeWidth;
        return {leftRelativeWidth, rightRelativeWidth, sharedRelativeWidth};
    }

    private calculateCursorConstraints(element: HTMLElement, prev: AssemblySpacingInput, next: AssemblySpacingInput) {
        return {
            left: prev.overflowSpace() ? element.parentElement.getBoundingClientRect().left : element.previousElementSibling.getBoundingClientRect().left - element.clientWidth/2,
            right: next.overflowSpace() ? element.parentElement.getBoundingClientRect().right : element.nextElementSibling.getBoundingClientRect().right - element.clientWidth/2
        };
    }

    private calculateAdjancedSpanValues(
        leftRelativeWidth:number,
        rightRelativeWidth: number,
        sharedRelativeWidth: number,
        prev: AssemblySpacingInput,
        next: AssemblySpacingInput,
        cursorX: number,
        element: HTMLElement,
        span: number
    ) {
        let newLeftSpan, newRightSpan;

        //calculate smaller span value first
        if(prev.span() < next.span()) {

            if(prev.overflowSpace() && next.span() === environment.FRAME_MIN_SPAN && cursorX > element.nextElementSibling.getBoundingClientRect().left ){
                newLeftSpan = prev.span();
            }else {
                newLeftSpan = prev.overflowSpace()
                    ? Math.min(Math.round( span * leftRelativeWidth / sharedRelativeWidth ), environment.FRAME_OVERFLOW_SPACE)
                    : Math.max(Math.round( span * leftRelativeWidth / sharedRelativeWidth ), environment.FRAME_MIN_SPAN);
            }

            newRightSpan = next.overflowSpace()
                ? Math.min(span - newLeftSpan, environment.FRAME_OVERFLOW_SPACE)
                : span - newLeftSpan;

        } else {

            if(next.overflowSpace() && prev.span() === environment.FRAME_MIN_SPAN && cursorX < element.previousElementSibling.getBoundingClientRect().right ){
                newRightSpan = next.span();

            }else{
                newRightSpan = next.overflowSpace()
                    ? Math.min(Math.round( span * rightRelativeWidth / sharedRelativeWidth ), environment.FRAME_OVERFLOW_SPACE)
                    : Math.max(Math.round( span * rightRelativeWidth / sharedRelativeWidth ), environment.FRAME_MIN_SPAN);

            }

            newLeftSpan = prev.overflowSpace()
                ? Math.min(span - newRightSpan, environment.FRAME_OVERFLOW_SPACE)
                : span - newRightSpan;

        }

        if(newLeftSpan+newRightSpan !== span){
            newLeftSpan >= newRightSpan
                ? newLeftSpan = newLeftSpan + ( newLeftSpan + newRightSpan - span )
                : newRightSpan = newRightSpan + ( newLeftSpan + newRightSpan - span );
        }

        return {newLeftSpan, newRightSpan};
    }

}